import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useRouteMatch from '../../../hooks/navigation/useRouteMatch';
import { SCREENS } from '../../../services/exports/Constants';
import useOffersTranslations from '../hooks/useOffersTranslations';
import useOffersStuff from '../hooks/useOffersStuff';
import useScreenType from '../../../hooks/utility/useScreenType';
import { ButtonTypes } from '../../../services/exports/Interfaces';
import CustomButton from '../../../components-library/buttons/CustomButton';
import classnames from 'classnames';
import ChevronLeftIcon from '../../../modules/mobile/assets/icons/ChevronLeftIcon';
import SuccessIcon from '../../../assets/icons/SuccessIcon';
import useTheme from '../../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { isNull } from 'lodash';

type AppliedOfferToastProps = {
  className?: string;
};

const AppliedOfferToast = ({ className }: AppliedOfferToastProps) => {
  const { appliedOffers } = useOffersStuff();
  const { toggleEligibleOffersModal } = useAppMethods();
  const routeMatch = useRouteMatch();
  const isMainScreen = routeMatch(SCREENS.HOME_SCREEN);
  const { isMobileApp } = useMobileApp();
  const { getLabelByOffer } = useOffersTranslations();
  const { isDesktop } = useScreenType();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:AppliedOfferToast' });
  const prefersReduced = useReducedMotion();
  const isVisibleMobileApp = isMobileApp && appliedOffers?.length;
  const isVisible = (appliedOffers?.length && isMainScreen) || isVisibleMobileApp;
  const offerCopy = appliedOffers?.length === 1 ? getLabelByOffer(appliedOffers[0]) : '';
  const copy = !isNull(offerCopy) && t('title', { offer: offerCopy, count: appliedOffers?.length });
  const transition = prefersReduced ? { duration: 0.2 } : { type: 'spring', stiffness: 420, damping: 32, bounce: 0.2 };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="applied-offer-toast"
          initial={{ y: 28, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 28, opacity: 0 }}
          transition={transition}
          className={classnames(
            {
              '!fixed bottom-5 mx-auto inset-x-0 !w-[430px] z-[1300]': isDesktop,
            },
            'transform-gpu will-change-transform',
            className,
          )}
        >
          <CustomButton
            onClick={() => toggleEligibleOffersModal({ openModal: true })}
            buttonType={ButtonTypes.primary}
            className={classnames(
              '!rounded-2xl transform hover:scale-[1.01] transition-all duration-300',
              'gap-x-md flex items-center justify-center shadow-lg font-medium border-[1px]',
              {
                'text-sm': !isDesktop,
                '!bg-[#0b462d] text-[#84e8c2] border-[#15a671]/20': isDarkMode,
                '!bg-[#e4fbed] text-[#0a4821] border-[#16a24999]/20': !isDarkMode,
              },
            )}
          >
            <SuccessIcon className="size-5 stroke-[#15a671]" />
            <span className="mx-auto absolute">{copy}</span>
            <ChevronLeftIcon className="rotate-180 stroke-1 ml-auto stroke-[#15a671]/60" />
          </CustomButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppliedOfferToast;
